/// <amd-module name="VSSUI/Context/MasterDetailsContext" />
import * as React from "react";
import { IObservableValue, IReadonlyObservableValue, ObservableValue } from "VSS/Core/Observable";
import { IItemProvider } from "VSSUI/Utilities/Provider";
import { ISelection, ISelectionRange } from "VSSUI/Utilities/Selection";
import { IDetailsAreaContent, IMasterDetailsContext, IMasterDetailsContextLayer, IMasterPanelContent } from "./MasterDetailsContext.Types";

export class BaseMasterDetailsContext implements IMasterDetailsContext {
    private readonly onExit: () => void;
    private readonly payload: IObservableValue<IMasterDetailsContextLayer>;
    private readonly payloadStack: IMasterDetailsContextLayer[];
    public hideDetailsPanel = new ObservableValue<boolean>(false);

    constructor(initialLayer: IMasterDetailsContextLayer, onExit: () => void) {
        this.payload = new ObservableValue<IMasterDetailsContextLayer>(initialLayer);
        this.payloadStack = [initialLayer];
        this.onExit = onExit;

        if (initialLayer.onPushed) {
            initialLayer.onPushed(this);
        }
    }

    public getCurrentLayer = () => {
        return this.payload as IReadonlyObservableValue<IMasterDetailsContextLayer>;
    };

    public getStack = () => {
        return this.payloadStack;
    };

    public push = (newLayer: IMasterDetailsContextLayer) => {
        this.payloadStack.push(newLayer);

        if (newLayer.onPushed) {
            newLayer.onPushed(this);
        }

        this.payload.value = this.payloadStack[this.payloadStack.length - 1];
    };

    public pop = () => {
        const returnVal = this.payloadStack.pop();

        if (returnVal && returnVal.onPopped) {
            returnVal.onPopped(this);
        }

        if (this.payloadStack.length > 0) {
            this.payload.value = this.payloadStack[this.payloadStack.length - 1];
        } else {
            this.onExit();
        }

        return returnVal;
    };

    public setDetailsPanelVisbility = (visible: boolean) => {
        this.hideDetailsPanel.value = !visible;
    };
}

export const MasterDetailsContext = React.createContext<IMasterDetailsContext>(
    new BaseMasterDetailsContext(
        {
            key: "",
            masterPanelContent: {},
            detailsContent: { renderContent: () => <div /> },
            selectedMasterItem: new ObservableValue(null)
        },
        () => undefined
    )
);

/**
 * Binds a selection, for things like Tree and List, to an Observable (usually IMasterDetailsLayer.selectedMasterItem)
 * When selection changes, it will update the observable
 * Will set selection to current payload item if it exists; works nicely with createChildPayload when drilling down
 * Supports single-select only
 */
export function bindSelectionToObservable<T>(selection: ISelection, itemProvider: IItemProvider<T>, observable: IObservableValue<T>) {
    const assignFunction = (selectedRanges: ISelectionRange[]) => {
        if (selectedRanges[0]) {
            const index = selectedRanges[0].beginIndex;
            observable.value = itemProvider.value[index];
        }
    };

    // Do this before we subscribe so we don't accidentally call assignFunction
    const matchingIndex = itemProvider.value.findIndex(x => x === observable.value);
    if (matchingIndex >= 0) {
        selection.select(matchingIndex);
    }

    selection.subscribe(assignFunction);
}

/**
 * Creates a layer that's a logical child of an existing layer
 * Useful for drill-down scenarios
 * Ensures correct typing of the child layer for common scenarios
 * @param key Unique identifier for the child layer
 * @param masterPanelDetails Master panel details for the child layer
 * @param detailsContent Detail rendering info for the child layer
 * @param initialSelectedItem Usually the clicked item from your current layer's detail view
 * @param parentLayer The parent layer, usually the currently displayed layer
 */
export function createChildLayer<T, P>(
    key: string,
    masterPanelDetails: IMasterPanelContent<T, P>,
    detailsContent: IDetailsAreaContent<T>,
    initialSelectedItem: T,
    parentLayer: IMasterDetailsContextLayer<P, any>,
    onPushed?: (context: IMasterDetailsContext) => void,
    ariaLabel?: string
): IMasterDetailsContextLayer<T, P> {
    return {
        key: key,
        masterPanelContent: masterPanelDetails,
        detailsContent: detailsContent,
        selectedMasterItem: new ObservableValue<T>(initialSelectedItem),
        parentItem: parentLayer.selectedMasterItem.value,
        onPushed: onPushed,
        ariaLabel: ariaLabel
    };
}
